import { type DataBlockArrayCreator } from './SHURDataBlockCreator'
import { HAKeys, type HALine } from 'shur-api'
import { fallbackNoDetailsAvailableDataSection } from './SHUREmptyDataCreator'
import { coverageConceptIconData } from './SHURDataboxData'
import type { DataBlock, IconDataSpan } from 'de-zurich-ui-library'
import { t } from '@/i18n'

const instantCoverageIconData: IconDataSpan = {
  iconClass: 'icon-vorteile',
  href: 'openModal_instantCoverage',
  isRouterLink: false
}

const selfHelpPlanningInfoIconData: IconDataSpan = {
  iconClass: 'icon-vorteile',
  href: 'openModal_selfHelpPlanningInfo',
  isRouterLink: false
}
const selfHelpConstructionInfoIconData: IconDataSpan = {
  iconClass: 'icon-vorteile',
  href: 'openModal_selfHelpConstructionInfo',
  isRouterLink: false
}

export class DataLiabilityPrivateCreator implements DataBlockArrayCreator {
  createDatablockArray(haLine: HALine): DataBlock[] {
    return [
      createPrivateLiabilityInsuredRisk(haLine),
      createPrivateLiabilityExtendedCoverage(haLine)
    ]
  }
}

export class DataLiabilityPetCreator implements DataBlockArrayCreator {
  createDatablockArray(haLine: HALine): DataBlock[] {
    return [createPetInsuranceBlock(haLine)]
  }
}

export class DataLiabilitySportBoatCreator implements DataBlockArrayCreator {
  createDatablockArray(haLine: HALine): DataBlock[] {
    return [createSportBoatInsuranceBlock(haLine)]
  }
}

export class DataLiabilityPropertyOwnersCreator implements DataBlockArrayCreator {
  createDatablockArray(haLine: HALine): DataBlock[] {
    return [
      createPropertyOwnersInsuranceBlock(haLine),
      createLiabilityInsuredPlace(haLine)
    ]
  }
}

export class DataLiabilityWaterPollutionCreator implements DataBlockArrayCreator {
  createDatablockArray(haLine: HALine): DataBlock[] {
    return [
      createWaterPollutionInsuranceBlock(haLine),
      createLiabilityInsuredPlace(haLine)
    ]
  }
}

export class DataLiabilityBuildersCreator implements DataBlockArrayCreator {
  createDatablockArray(haLine: HALine): DataBlock[] {
    return [createBuildersInsuranceBlock(haLine), createLiabilityInsuredPlace(haLine)]
  }
}

// Helper to avoid duplicated CoverageConcept data entries
function createCoverageConceptEntry(
  haLine: HALine,
  details: NonNullable<HALine['details']>,
  withIconSpan = true
) {
  const hasCoverageConcept = details.some((entry) => entry.key === HAKeys.CoverageConcept)
  return {
    label: t('shurDetails.sections.coverageConcept.title'),
    value: hasCoverageConcept
      ? t(
          `shurDetails.sections.coverageConcept.${
            details.find((entry) => entry.key === HAKeys.CoverageConcept)!.value
          }`
        )
      : null,
    iconSpan:
      withIconSpan && hasCoverageConcept
        ? coverageConceptIconData(
            String(details.find((entry) => entry.key === HAKeys.CoverageConcept)?.value),
            haLine.productSubType,
            String(details.find((entry) => entry.key === HAKeys.ProductVersion)?.value)
          )
        : null
  }
}

// when no details are provided
function fallbackDataSection(titleLabelPath: string): DataBlock {
  return {
    title: {
      label: t(titleLabelPath).toString() ?? titleLabelPath
    },
    data: [
      {
        label: 'null',
        value: null
      }
    ]
  }
}

// Versicherungsort
function createLiabilityInsuredPlace(haLine: HALine): DataBlock {
  if (!haLine.details || haLine.details.length === 0)
    return fallbackNoDetailsAvailableDataSection()
  const contractDetailsCodeValuePairs = haLine.details

  return <DataBlock>{
    title: {
      label: t('shurDetails.sections.insuredPlace.titleHA')
    },
    data: [
      {
        label: t(`shurDetails.sections.insuredPlace.${HAKeys.StreetAndNumber}`),
        value: contractDetailsCodeValuePairs.find(
          (entry) => entry.key === HAKeys.StreetAndNumber
        )?.value
      },
      {
        label: t(`shurDetails.sections.insuredPlace.${HAKeys.ZipcodeAndCity}`),
        value: contractDetailsCodeValuePairs.find(
          (entry) => entry.key === HAKeys.ZipcodeAndCity
        )?.value
      },
      {
        label: t(`shurDetails.sections.propertyOwnersInsurance.${HAKeys.LivingArea}`),
        value: contractDetailsCodeValuePairs.find(
          (entry) => entry.key === HAKeys.LivingArea
        )?.value
      },
      {
        label: t(`shurDetails.sections.propertyOwnersInsurance.${HAKeys.LandArea}`),
        value: contractDetailsCodeValuePairs.find(
          (entry) => entry.key === HAKeys.LandArea
        )?.value
      },
      {
        label: t(
          `shurDetails.sections.propertyOwnersInsurance.${HAKeys.TypeOfRealEstate}`
        ),
        value: contractDetailsCodeValuePairs.find(
          (entry) => entry.key === HAKeys.TypeOfRealEstate
        )?.value
      }
    ]
  }
}

// Privat-Haftpflichtversicherung
// PHV - Versichertes Risiko
function createPrivateLiabilityInsuredRisk(haLine: HALine): DataBlock {
  if (!haLine.details || haLine.details.length === 0)
    return fallbackNoDetailsAvailableDataSection()
  const contractDetailsCodeValuePairs = haLine.details

  return <DataBlock>{
    data: [
      createCoverageConceptEntry(haLine, contractDetailsCodeValuePairs),
      {
        label: t(
          `shurDetails.sections.privateLiabilityInsurance.${HAKeys.TargetGroup}.title`
        ),
        value: contractDetailsCodeValuePairs.some(
          (entry) => entry.key === HAKeys.TargetGroup
        )
          ? t(
              `shurDetails.sections.privateLiabilityInsurance.${HAKeys.TargetGroup}.${
                contractDetailsCodeValuePairs.find(
                  (entry) => entry.key === HAKeys.TargetGroup
                )!.value
              }`
            )
          : null
      },
      {
        label: t(`shurDetails.sections.${HAKeys.Deductible}.title`),
        value: contractDetailsCodeValuePairs.find(
          (entry) => entry.key === HAKeys.Deductible
        )?.value
      },
      {
        label: t(`shurDetails.sections.${HAKeys.InstantCoverage}`),
        value: contractDetailsCodeValuePairs.find(
          (entry) => entry.key === HAKeys.InstantCoverage
        )?.value,
        iconSpan: instantCoverageIconData
      }
    ]
  }
}
// PHV - Deckungserweiterung
function createPrivateLiabilityExtendedCoverage(haLine: HALine): DataBlock {
  if (!haLine.details || haLine.details.length === 0)
    return fallbackNoDetailsAvailableDataSection()
  const contractDetailsCodeValuePairs = haLine.details

  return <DataBlock>{
    title: {
      label: t('shurDetails.sections.privateLiabilityInsurance.extendedCoverage')
    },
    data: [
      {
        label: t(
          `shurDetails.sections.privateLiabilityInsurance.${HAKeys.PublicServiceLiability}`
        ),
        value: contractDetailsCodeValuePairs.some(
          (entry) => entry.key === HAKeys.PublicServiceLiability
        )
          ? t(`shurDetails.sections.privateLiabilityInsurance.included`)
          : null
      },
      {
        label: t(`shurDetails.sections.privateLiabilityInsurance.${HAKeys.Drone}`),
        value: contractDetailsCodeValuePairs.some((entry) => entry.key === HAKeys.Drone)
          ? t(`shurDetails.sections.privateLiabilityInsurance.included`)
          : null
      }
    ]
  }
}

// Tierhalter-Haftpflichtversicherung
function createPetInsuranceBlock(haLine: HALine): DataBlock {
  if (!haLine.details || haLine.details.length === 0)
    return fallbackNoDetailsAvailableDataSection()
  const contractDetailsCodeValuePairs = haLine.details

  return <DataBlock>{
    title: {
      label: t('shurDetails.sections.insuredRisks')
    },
    data: [
      createCoverageConceptEntry(haLine, contractDetailsCodeValuePairs),
      {
        label: t(`shurDetails.sections.petInsurance.${HAKeys.DogRace}`),
        value: contractDetailsCodeValuePairs.find((entry) => entry.key === HAKeys.DogRace)
          ?.value
      },
      {
        label: t(`shurDetails.sections.petInsurance.${HAKeys.DangerousDogRace}`),
        value: contractDetailsCodeValuePairs.find(
          (entry) => entry.key === HAKeys.DangerousDogRace
        )?.value
      },
      {
        label: t(`shurDetails.sections.petInsurance.${HAKeys.HorseRace}`),
        value: contractDetailsCodeValuePairs.find(
          (entry) => entry.key === HAKeys.HorseRace
        )?.value
      },
      {
        label: t(`shurDetails.sections.petInsurance.${HAKeys.GraceHorseRace}`),
        value: contractDetailsCodeValuePairs.find(
          (entry) => entry.key === HAKeys.GraceHorseRace
        )?.value
      },
      {
        label: t(`shurDetails.sections.petInsurance.${HAKeys.HorseSharing}`),
        value: contractDetailsCodeValuePairs.find(
          (entry) => entry.key === HAKeys.HorseSharing
        )?.value
      },
      {
        label: t(`shurDetails.sections.${HAKeys.Deductible}.title`),
        value: contractDetailsCodeValuePairs.find(
          (entry) => entry.key === HAKeys.Deductible
        )?.value
      },
      {
        label: t(`shurDetails.sections.${HAKeys.InstantCoverage}`),
        value: contractDetailsCodeValuePairs.find(
          (entry) => entry.key === HAKeys.InstantCoverage
        )?.value,
        iconSpan: instantCoverageIconData
      }
    ]
  }
}

// Sportboot-Haftpflichtversicherung
function createSportBoatInsuranceBlock(haLine: HALine): DataBlock {
  if (!haLine.details || haLine.details.length === 0)
    return fallbackNoDetailsAvailableDataSection()
  const contractDetailsCodeValuePairs = haLine.details

  return <DataBlock>{
    title: {
      label: t('shurDetails.sections.insuredRisks')
    },
    data: [
      createCoverageConceptEntry(haLine, contractDetailsCodeValuePairs),
      {
        label: t(`shurDetails.sections.sportBoatInsurance.${HAKeys.MotorBoat}`),
        value: contractDetailsCodeValuePairs.find(
          (entry) => entry.key === HAKeys.MotorBoat
        )?.value
      },
      {
        label: t(`shurDetails.sections.sportBoatInsurance.${HAKeys.Sailboard}`),
        value: contractDetailsCodeValuePairs.find(
          (entry) => entry.key === HAKeys.Sailboard
        )?.value
      },
      {
        label: t(`shurDetails.sections.sportBoatInsurance.${HAKeys.Jetski}`),
        value: contractDetailsCodeValuePairs.find((entry) => entry.key === HAKeys.Jetski)
          ?.value
      },
      {
        label: t(`shurDetails.sections.sportBoatInsurance.${HAKeys.SailingBoat}`),
        value: contractDetailsCodeValuePairs.find(
          (entry) => entry.key === HAKeys.SailingBoat
        )?.value
      },
      {
        label: t(`shurDetails.sections.sportBoatInsurance.${HAKeys.Canoe}`),
        value: contractDetailsCodeValuePairs.find((entry) => entry.key === HAKeys.Canoe)
          ?.value
      },
      {
        label: t(`shurDetails.sections.${HAKeys.Deductible}.title`),
        value: contractDetailsCodeValuePairs.find(
          (entry) => entry.key === HAKeys.Deductible
        )?.value
      },
      {
        label: t(`shurDetails.sections.${HAKeys.InstantCoverage}`),
        value: contractDetailsCodeValuePairs.find(
          (entry) => entry.key === HAKeys.InstantCoverage
        )?.value,
        iconSpan: instantCoverageIconData
      }
    ]
  }
}

// Haus- & Grundbesitzer- Haftpflichtversicherung
function createPropertyOwnersInsuranceBlock(haLine: HALine): DataBlock {
  if (!haLine.details || haLine.details.length === 0)
    return fallbackNoDetailsAvailableDataSection()
  const contractDetailsCodeValuePairs = haLine.details

  return <DataBlock>{
    title: {
      label: t('shurDetails.sections.insuredRisks')
    },
    data: [
      createCoverageConceptEntry(haLine, contractDetailsCodeValuePairs),
      {
        label: t(`shurDetails.sections.${HAKeys.Deductible}.title`),
        value: contractDetailsCodeValuePairs.find(
          (entry) => entry.key === HAKeys.Deductible
        )?.value
      },
      {
        label: t(`shurDetails.sections.${HAKeys.InstantCoverage}`),
        value: contractDetailsCodeValuePairs.find(
          (entry) => entry.key === HAKeys.InstantCoverage
        )?.value,
        iconSpan: instantCoverageIconData
      }
    ]
  }
}

// Gewässerschaden-Haftpflichtversicherung
function createWaterPollutionInsuranceBlock(haLine: HALine): DataBlock {
  if (!haLine.details || haLine.details.length === 0) return fallbackDataSection('')
  const contractDetailsCodeValuePairs = haLine.details

  if (!contractDetailsCodeValuePairs)
    return fallbackDataSection('shurDetails.sections.insuredRisks')

  return <DataBlock>{
    title: {
      label: t('shurDetails.sections.insuredRisks')
    },
    data: [
      createCoverageConceptEntry(haLine, contractDetailsCodeValuePairs),
      {
        label: t(`shurDetails.sections.waterDamageInsurance.${HAKeys.SmallTank}`),
        value: contractDetailsCodeValuePairs.find(
          (entry) => entry.key === HAKeys.SmallTank
        )?.value
      },
      {
        label: t(`shurDetails.sections.waterDamageInsurance.${HAKeys.TypeOfTank}`),
        value: contractDetailsCodeValuePairs.find(
          (entry) => entry.key === HAKeys.TypeOfTank
        )?.value
      },
      {
        label: t(`shurDetails.sections.waterDamageInsurance.${HAKeys.TankVolume}`),
        value: contractDetailsCodeValuePairs.find(
          (entry) => entry.key === HAKeys.TankVolume
        )?.value
      },
      {
        label: t(`shurDetails.sections.${HAKeys.Deductible}.title`),
        value: contractDetailsCodeValuePairs.find(
          (entry) => entry.key === HAKeys.Deductible
        )?.value
      },
      {
        label: t(`shurDetails.sections.${HAKeys.InstantCoverage}`),
        value: contractDetailsCodeValuePairs.find(
          (entry) => entry.key === HAKeys.InstantCoverage
        )?.value,
        iconSpan: instantCoverageIconData
      }
    ]
  }
}

function createBuildersInsuranceBlock(haLine: HALine): DataBlock {
  if (!haLine.details || haLine.details.length === 0) return fallbackDataSection('')
  const contractDetailsCodeValuePairs = haLine.details

  if (!contractDetailsCodeValuePairs)
    return fallbackDataSection('shurDetails.sections.buildersInsurance.scopeOfInsurance')

  return <DataBlock>{
    title: {
      label: t('shurDetails.sections.buildersInsurance.scopeOfInsurance')
    },
    data: [
      createCoverageConceptEntry(haLine, contractDetailsCodeValuePairs, false),
      {
        label: t(`shurDetails.sections.buildersInsurance.insuredAmount`),
        value: contractDetailsCodeValuePairs.find(
          (entry) => entry.key === HAKeys.InsuredAmount
        )?.value
      },
      {
        label: t(`shurDetails.sections.buildersInsurance.precautionAmount`),
        value: contractDetailsCodeValuePairs.find(
          (entry) => entry.key === HAKeys.PrecautionAmount
        )?.value
      },
      {
        label: t(`shurDetails.sections.buildersInsurance.constructionEmployee`),
        value: contractDetailsCodeValuePairs.find(
          (entry) => entry.key === HAKeys.ConstructionEmployee
        )?.value
      },
      {
        label: t(`shurDetails.sections.buildersInsurance.waterDamage`),
        value: contractDetailsCodeValuePairs.find(
          (entry) => entry.key === HAKeys.WaterDamage
        )?.value
      },
      {
        label: t(`shurDetails.sections.buildersInsurance.environmentalDamage`),
        value: contractDetailsCodeValuePairs.find(
          (entry) => entry.key === HAKeys.EnvironmentalDamage
        )?.value
      },
      {
        label: t(`shurDetails.sections.buildersInsurance.selfHelpPlanning`),
        value: contractDetailsCodeValuePairs.find(
          (entry) => entry.key === HAKeys.SelfHelpPlanning
        )?.value,
        iconSpan: selfHelpPlanningInfoIconData
      },
      {
        label: t(`shurDetails.sections.buildersInsurance.selfHelpConstruction`),
        value: contractDetailsCodeValuePairs.find(
          (entry) => entry.key === HAKeys.SelfHelpConstruction
        )?.value,
        iconSpan: selfHelpConstructionInfoIconData
      }
    ]
  }
}

export { fallbackDataSection }
